// Per-model detail: the performance matrix + per-phase throughput + run history.
import { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { api, type ModelResult } from '../api';
import Heatmap from '../components/Heatmap';
import PhasePanel from '../components/PhasePanel';
import MatrixTimingTable from '../components/MatrixTimingTable';

export default function ModelDetail() {
  const { name = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const runId = params.get('runId') ?? undefined;

  const [data, setData] = useState<ModelResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setData(null);
    setError(null);
    api
      .modelResult(name, runId)
      .then(setData)
      .catch((e) => setError(e instanceof Error ? e.message : 'Error'));
  }, [name, runId]);

  if (error) return <div className="banner error">{error}</div>;
  if (!data) return <div className="loading">Loading…</div>;

  return (
    <section>
      <div className="page-head">
        <div>
          <Link to="/" className="muted">
            ← Overview
          </Link>
          <h1>{data.model}</h1>
          <p className="muted small">
            KV limit: {data.maxKv ?? '–'} · run {data.runId ?? '–'} · status {data.status ?? '–'}
            {data.finishedAt ? ` · ${new Date(data.finishedAt).toLocaleString()}` : ''}
            {` · hardware: ${data.hardwareLabel ?? '—'}`}
          </p>
          {data.benchmarks ? (
            <p className="muted small">
              Published:{' '}
              {Object.entries(data.benchmarks)
                .map(([k, v]) => `${k} ${v.toFixed(1)}%`)
                .join(' · ')}
            </p>
          ) : null}
        </div>
        {data.history.length > 1 ? (
          <label className="run-select">
            Run:
            <select
              value={runId ?? data.runId ?? ''}
              onChange={(e) => {
                const next = new URLSearchParams(params);
                next.set('runId', e.target.value);
                setParams(next);
              }}
            >
              {data.history.map((h) => (
                <option key={h.runId} value={h.runId}>
                  {new Date(h.queuedAt).toLocaleString()} · {h.status}
                  {h.score != null ? ` · ${h.score.toFixed(1)}` : ''}
                </option>
              ))}
            </select>
          </label>
        ) : null}
      </div>

      {data.status === 'error' || data.error ? (
        <div className="banner error">
          Model run failed{data.error ? `: ${data.error}` : ''}
        </div>
      ) : null}

      {(() => {
        const failed = data.cells.filter((c) => c.errored);
        if (failed.length === 0) return null;
        return (
          <div className="banner error failures">
            <strong>
              {failed.length} of {data.summary?.cellsTotal ?? data.cells.length} cells failed:
            </strong>
            <ul>
              {failed.map((c) => (
                <li key={`${c.inputTokens}x${c.outputTokens}`}>
                  input {c.inputTokens} / output {c.outputTokens} —{' '}
                  {c.errorReason ?? 'the model returned no usable output'}
                </li>
              ))}
            </ul>
          </div>
        );
      })()}

      {data.cells.length === 0 ? (
        <p className="muted">No measurements for this model yet.</p>
      ) : (
        <>
          <PhasePanel summary={data.summary} />
          <Heatmap
            inputTokens={data.inputTokens}
            outputTokens={data.outputTokens}
            cells={data.cells}
            colorMetric={data.colorMetric}
            valueRange={data.valueRange}
          />
          <MatrixTimingTable
            inputTokens={data.inputTokens}
            outputTokens={data.outputTokens}
            cells={data.cells}
          />
        </>
      )}
    </section>
  );
}
